/**
 * PreviewPane — live resolved spec-sheet preview for a Product Specs family.
 *
 * Pick a sample SKU/variant → POST the DRAFT data to the backend resolver
 * (previewFamily) → render the returned `{html}` in a sandboxed iframe. The
 * iframe approach mirrors ProposalBuilderPage.tsx's PreviewDrawer (proposal CSS
 * stays isolated from the admin shell).
 *
 * Sample SKUs are derived from the draft data per shape so the dropdown always
 * matches what's editable: HP `models`/`variants` keys, SWH series, tank
 * grade×capacity combos, datasheet SKUs, fixed-page families a single sentinel.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Eye, RotateCcw } from 'lucide-react';
import { previewFamily, type EditorShape, type FamilyKey } from '../../../api/productSpecs';

/** Derive the candidate sample SKUs/variants for the dropdown from the draft. */
function sampleSkus(shape: EditorShape, familyKey: string, data: any): string[] {
  try {
    switch (shape) {
      case 'hp_model': {
        const models = data?.models ?? data?.variants;
        if (models && typeof models === 'object' && !Array.isArray(models)) return Object.keys(models);
        return [];
      }
      case 'datasheet_list':
        return data && typeof data === 'object' && !Array.isArray(data) ? Object.keys(data) : [];
      case 'swh_series':
        return Array.isArray(data?.variants)
          ? data.variants.map((v: any) => String(v?.series ?? '')).filter(Boolean)
          : [];
      case 'tank_variant': {
        // Build grade · capacity sample keys (resolver matches on SKU text).
        const grades: string[] = Array.isArray(data?.variants)
          ? data.variants.map((v: any) => String(v?.grade ?? '')).filter(Boolean)
          : [];
        const caps: number[] = Array.isArray(data?.capacities) ? data.capacities : [250, 1000, 2000];
        const out: string[] = [];
        for (const g of grades.slice(0, 3)) for (const c of caps.slice(0, 3)) out.push(`${c}L ${g}`);
        return out.length ? out : grades;
      }
      case 'fixed_page':
        // Fixed pages resolve from the family itself; a sentinel triggers a render.
        return [familyKey];
      default:
        return [];
    }
  } catch {
    return [];
  }
}

export function PreviewPane({
  familyKey,
  shape,
  data,
}: {
  familyKey: FamilyKey | string;
  shape: EditorShape;
  data: any;
}) {
  const skus = useMemo(() => sampleSkus(shape, familyKey, data), [shape, familyKey, data]);
  const [sku, setSku] = useState<string>(skus[0] ?? '');
  const [html, setHtml] = useState<string | null>(null);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Keep the latest draft in a ref so the debounce closure reads fresh data.
  const dataRef = useRef(data);
  dataRef.current = data;

  // Keep a valid selection as the SKU set changes.
  useEffect(() => {
    if (skus.length && !skus.includes(sku)) setSku(skus[0]);
    if (!skus.length && sku) setSku('');
  }, [skus.join('|')]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = useCallback(async (which: string) => {
    if (!which) return;
    setFetching(true);
    setError(null);
    try {
      const res = await previewFamily(familyKey, which, dataRef.current);
      setHtml(res.html);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.message ?? 'Preview failed');
      setHtml(null);
    } finally {
      setFetching(false);
    }
  }, [familyKey]);

  // Auto-refresh (debounced) whenever the SKU or the draft data changes, so the
  // author sees edits resolve live without clicking Refresh.
  useEffect(() => {
    if (!sku) {
      setHtml(null);
      return;
    }
    const t = setTimeout(() => run(sku), 450);
    return () => clearTimeout(t);
  }, [sku, data, run]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* header */}
      <div
        style={{
          padding: '10px 14px',
          borderBottom: '1px solid var(--line)',
          display: 'flex',
          alignItems: 'center',
          gap: 9,
          flexShrink: 0,
          background: 'var(--ivory-0)',
        }}
      >
        <Eye size={15} style={{ color: 'var(--ink-2)' }} />
        <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink-1)', flexShrink: 0 }}>
          Live preview
        </div>
        <select
          value={sku}
          onChange={(e) => setSku(e.target.value)}
          disabled={skus.length === 0}
          style={{
            flex: 1,
            minWidth: 0,
            height: 30,
            border: '1px solid var(--line)',
            borderRadius: 7,
            background: 'var(--ivory-0)',
            fontSize: 12,
            color: 'var(--ink-1)',
            padding: '0 8px',
            cursor: skus.length ? 'pointer' : 'not-allowed',
          }}
        >
          {skus.length === 0 && <option value="">No sample SKU</option>}
          {skus.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => run(sku)}
          disabled={fetching || !sku}
          title="Refresh preview"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            height: 30,
            padding: '0 10px',
            borderRadius: 7,
            fontSize: 12,
            fontWeight: 500,
            border: '1px solid var(--line)',
            background: 'var(--ivory-0)',
            color: fetching ? 'var(--ink-3)' : 'var(--ink-2)',
            cursor: fetching || !sku ? 'not-allowed' : 'pointer',
            flexShrink: 0,
          }}
        >
          <RotateCcw size={12} style={{ opacity: fetching ? 0.5 : 1 }} />
          {fetching ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {/* body */}
      <div style={{ flex: 1, minHeight: 0, position: 'relative', background: 'var(--ivory-2)' }}>
        {error && !fetching && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              padding: 24,
            }}
          >
            <p style={{ margin: 0, fontSize: 12.5, color: '#b91c1c', textAlign: 'center' }}>{error}</p>
            <button
              type="button"
              onClick={() => run(sku)}
              style={{
                height: 30,
                padding: '0 12px',
                borderRadius: 7,
                fontSize: 12,
                border: '1px solid var(--line)',
                background: 'var(--ivory-0)',
                color: 'var(--ink-2)',
                cursor: 'pointer',
              }}
            >
              Retry
            </button>
          </div>
        )}

        {html && !error && (
          <iframe
            srcDoc={html}
            sandbox="allow-same-origin"
            title={`Spec preview — ${sku}`}
            style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }}
          />
        )}

        {!html && !error && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--ink-3)',
              fontSize: 12.5,
            }}
          >
            {fetching ? 'Generating preview…' : skus.length ? 'Pick a sample SKU to preview.' : 'No sample SKU to preview.'}
          </div>
        )}
      </div>
    </div>
  );
}
